import React from 'react'
import { Link } from 'react-router-dom'
import { Card, Typography, Descriptions } from 'antd'
import { ContainerOutlined, EyeOutlined, ScheduleOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { PageHeader } from '../../design-system/PageHeader'
import { FrontendUrls } from '../../types/urls'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { roleAdmin, selectAuthUserSlice } from '../../store/auth-user-slice'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { useShouldEnableFeatures } from '../../store/use-should-enable-features'
import FreemiumPricingSimpleTooltip from '../../components/freemium-pricing-simple-tooltip'
import VotitoSurveyConnected from '../../components/votito-survey/VotitoSurveyConnected'

const REPORTS_SURVEY_ID = '266ca3b6-bbf3-483f-8fec-7d792c293a38'

const { Paragraph } = Typography

const ReportsPage = () => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const userIsAdmin = useSelector(roleAdmin)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const isStrictAdmin = authUser.role?.toLowerCase() === 'admin'
  // Same plan-or-flag check the ScheduledReportsPage itself uses - the card must not
  // stay disabled for companies that got the feature via the scheduled-reports flag.
  const shouldEnableScheduledReports = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.scheduledReports)

  return (
    <div className="main-content">
      <PageHeader title={<IntlMessages id="sidebar.reports" />} actions={<VotitoSurveyConnected surveyId={REPORTS_SURVEY_ID} />} />
      <div className="main-content-body" id="vt-cards">
        <Paragraph className="vt-cards-intro">
          <IntlMessages id="components.reports.intro" />
        </Paragraph>
        <div className="vt-cards-container">
          <ReportDescription
            header={<IntlMessages id="export.leaveHistoryReport" />}
            content={<IntlMessages id="export.leaveHistoryReportDescription" />}
            path={`${FrontendUrls.reports}/leave-history-report`}
            actionText={<IntlMessages id="components.reports.generate" />}
            actionIcon={<ContainerOutlined />}
          />
          <ReportDescription
            header={<IntlMessages id="export.leaveBalanceReport" />}
            content={<IntlMessages id="export.leaveBalanceReportDescription" />}
            path={`${FrontendUrls.reports}/leave-balance-report`}
            actionText={<IntlMessages id="components.reports.generate" />}
            actionIcon={<ContainerOutlined />}
          />
          <FreemiumPricingSimpleTooltip isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium} featureName="monthly_balance_report" featureCategory="reports">
            <ReportDescription
              header={<IntlMessages id="export.monthlyLeaveBalanceReport" />}
              content={<IntlMessages id="export.monthlyLeaveBalanceReportDescription" />}
              // eslint-disable-next-line max-len
              path={`${FrontendUrls.reports}/leave-balance-monthly-report`}
              actionText={<IntlMessages id="components.reports.generate" />}
              actionIcon={<ContainerOutlined />}
              disabled={authCompany?.plan === SubscriptionPlanEnum.freemium}
            />
          </FreemiumPricingSimpleTooltip>
          {isStrictAdmin && (
            <FreemiumPricingSimpleTooltip isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium} featureName="company_logs" featureCategory="reports">
              <ReportDescription
                header={<IntlMessages id="components.reports.companyLogs" />}
                content={<IntlMessages id="components.reports.companyLogsDescription" />}
                path={FrontendUrls.companyLogs}
                actionText={<IntlMessages id="app.view" />}
                actionIcon={<EyeOutlined />}
                disabled={authCompany?.plan === SubscriptionPlanEnum.freemium}
              />
            </FreemiumPricingSimpleTooltip>
          )}
          {userIsAdmin && (
            <FreemiumPricingSimpleTooltip
              isFreemiumPlan={authCompany?.plan === SubscriptionPlanEnum.freemium && !shouldEnableScheduledReports}
              onCorePlan={false}
              featureName="scheduled_reports"
              featureCategory="reports"
            >
              <ReportDescription
                header={<IntlMessages id="sidebar.scheduledReports" />}
                content={<IntlMessages id="components.reports.scheduledReportsDescription" />}
                path={FrontendUrls.scheduledReports}
                actionText={<IntlMessages id="scheduledReportForm.saveReport" />}
                actionIcon={<ScheduleOutlined />}
                disabled={!shouldEnableScheduledReports}
              />
            </FreemiumPricingSimpleTooltip>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReportsPage

const ReportDescription = ({ header, content, path, actionText, actionIcon, disabled = false }) => {
  const CardContent = (
    <Card
      className={`vt-card ${disabled ? 'vt-card-disabled' : ''}`}
      hoverable={!disabled}
      key={path}
      actions={[
        <div key={path} data-disabled={disabled} onClick={(e) => disabled && e.stopPropagation()}>
          {actionIcon}&nbsp;{actionText}
        </div>,
      ]}
      title={header}
    >
      <Descriptions layout="horizontal" size="small" column={1}>
        <Descriptions.Item key="desc">{content}</Descriptions.Item>
      </Descriptions>
    </Card>
  )

  return disabled ? (
    <div className="cards-feedback">{CardContent}</div>
  ) : (
    <Link to={path} className="cards-feedback">
      {CardContent}
    </Link>
  )
}
